import { midiToNoteName, SYNTH_PITCH_ROW_HEIGHT } from '../../stores/synthModel.js'
import { eventDurationMs, ticksToMs } from '../../stores/synthTiming.js'

export const SYNTH_SEQUENCE_SNAP_MS = 25
export const SYNTH_SEQUENCE_DEFAULT_MIN_MIDI = 48
export const SYNTH_SEQUENCE_DEFAULT_MAX_MIDI = 72

export function buildPitchRows(minMidi, maxMidi) {
  const rows = []
  let index = 0
  for (let midi = maxMidi; midi >= minMidi; midi--, index++) {
    rows.push({
      index,
      midi,
      noteName: midiToNoteName(midi),
      isWhite: isWhiteKey(midi),
      isOctave: midi % 12 === 0,
      y: index * SYNTH_PITCH_ROW_HEIGHT,
    })
  }
  return rows
}

export function isWhiteKey(midi) {
  return [0, 2, 4, 5, 7, 9, 11].includes(Number(midi) % 12)
}

export function midiToRowIndex(midi, minMidi, maxMidi) {
  const rows = buildPitchRows(minMidi, maxMidi)
  const idx = rows.findIndex(row => row.midi === midi)
  return idx >= 0 ? idx : 0
}

export function rowIndexToMidi(rowIndex, minMidi, maxMidi) {
  const midi = maxMidi - rowIndex
  return clampMidi(midi)
}

export function cumulativeStarts(sequence, bpm = 120) {
  let legacyCursor = 0
  return (sequence ?? []).map(event => {
    const startTick = Number.isFinite(Number(event?.startTick)) ? Number(event.startTick) : legacyCursor
    legacyCursor = startTick + Math.max(1, Number(event?.durationTicks ?? event?.durationMs) || 0)
    return ticksToMs(startTick, bpm)
  })
}

export function buildAbsoluteSequenceTiming(sequence, bpm = 120) {
  let maxEndMs = 0
  let legacyCursorTick = 0
  return (sequence ?? []).map((event, eventIndex) => {
    const durationTicks = Math.max(1, Number(event?.durationTicks ?? event?.durationMs) || 0)
    const startTick = Number.isFinite(Number(event?.startTick)) ? Math.max(0, Number(event.startTick)) : legacyCursorTick
    legacyCursorTick = startTick + durationTicks
    const durationMs = ticksToMs(durationTicks, bpm)
    const startMs = ticksToMs(startTick, bpm)
    const timed = {
      eventIndex,
      note: event?.note,
      durationMs,
      durationTicks,
      startTick,
      endTick: startTick + durationTicks,
      startMs,
      endMs: startMs + durationMs,
    }
    maxEndMs = Math.max(maxEndMs, timed.endMs)
    Object.defineProperty(timed, 'maxEndMs', { value: maxEndMs, enumerable: false })
    return timed
  })
}

export function findTimelineEventIndex(events, timeMs) {
  return filterTimelineRange(events, timeMs, Number(timeMs) + 0.001)[0]?.eventIndex ?? -1
}

export function advanceTimelineEventIndex(events, timeMs, currentIndex = -1) {
  return findTimelineEventIndex(events, timeMs)
}

export function filterTimelineRange(events, startMs, endMs, overscanMs = 0) {
  if (!events?.length) return []
  const start = Math.max(0, Number(startMs) - Math.max(0, Number(overscanMs) || 0))
  const end = Math.max(start, Number(endMs) + Math.max(0, Number(overscanMs) || 0))
  let low = 0
  let high = events.length
  while (low < high) {
    const middle = (low + high) >> 1
    if (events[middle].maxEndMs <= start) low = middle + 1
    else high = middle
  }
  const visible = []
  for (let index = low; index < events.length && events[index].startMs < end; index++) visible.push(events[index])
  return visible
}

export function pianoRollEventY(startMs, playbackTimeMs, pixelsPerMs, triggerY = 0) {
  return Number(triggerY) + (Number(startMs) - Number(playbackTimeMs)) * Number(pixelsPerMs)
}

export function pianoRollSurfaceHeight({ playing, viewportHeight, totalDurationMs, pixelsPerMs, pitchRangeHeight, tailPadding = 80 }) {
  const viewport = Math.max(0, Number(viewportHeight) || 0)
  if (playing) return viewport
  const timelineHeight = Math.max(0, Number(totalDurationMs) || 0) * Math.max(0, Number(pixelsPerMs) || 0) + tailPadding
  const virtualPitchHeight = Math.max(0, Number(pitchRangeHeight) || 0)
  return Math.max(viewport + 1, timelineHeight, virtualPitchHeight)
}

export function pianoRollNoteX(midi, minMidi, keyWidth) {
  return (Number(midi) - Number(minMidi)) * Number(keyWidth)
}

export function sequencePlaybackX(startMs, playbackTimeMs, pixelsPerMs, playheadX) {
  return Number(playheadX) + (Number(startMs) - Number(playbackTimeMs)) * Number(pixelsPerMs)
}

export function sequenceXToTimeMs(x, playbackTimeMs, pixelsPerMs, playheadX) {
  const scale = Number(pixelsPerMs)
  if (!scale) return 0
  return Math.max(0, Number(playbackTimeMs) + (Number(x) - Number(playheadX)) / scale)
}

// Keeps a content-space x inside the scrolled viewport, leaving a right-hand margin so the
// next steps stay visible. Returns null when the position already sits in the safe zone.
export function followScrollLeft(cursorX, { scrollLeft = 0, viewportWidth = 0, leftInset = 0, rightMargin = 0 } = {}) {
  const x = Number(cursorX) || 0
  const width = Math.max(0, Number(viewportWidth) || 0)
  const inset = Math.max(0, Number(leftInset) || 0)
  const margin = Math.max(0, Math.min(Number(rightMargin) || 0, Math.max(0, width - inset)))
  const current = Math.max(0, Number(scrollLeft) || 0)
  if (width <= inset) return null
  if (x > current + width - margin) return Math.max(0, x - width + margin)
  if (x < current + inset) return Math.max(0, x - inset)
  return null
}

export function sequenceEventWidth(durationMs, pixelsPerMs) {
  return Math.max(2, Number(durationMs) * Number(pixelsPerMs))
}

export function isTimedEventActive(event, playbackTimeMs) {
  const time = Number(playbackTimeMs)
  return event?.note != null && event.startMs <= time && time < event.endMs
}

export function timeToInsertIndex(sequence, timeMs, bpm = 120) {
  const starts = cumulativeStarts(sequence, bpm)
  const target = Math.max(0, Number(timeMs) || 0)
  let bestIndex = sequence?.length ? sequence.length - 1 : -1
  let bestDistance = Number.POSITIVE_INFINITY
  for (let i = 0; i < starts.length; i++) {
    const start = starts[i]
    const end = start + eventDurationMs(sequence[i], bpm)
    const boundary = Math.abs(target - start) <= Math.abs(target - end) ? start : end
    const distance = Math.abs(target - boundary)
    if (distance < bestDistance) {
      bestDistance = distance
      bestIndex = target < boundary ? i - 1 : i
    }
  }
  return Math.max(-1, Math.min(sequence.length - 1, bestIndex))
}

export function durationToWidthPercent(durationMs, totalMs) {
  const total = Math.max(1, Number(totalMs) || 0)
  return Math.max(2, (Math.max(1, Number(durationMs) || 0) / total) * 100)
}

export function clampDurationMs(durationMs, minDurationMs = 1) {
  return Math.max(Math.max(1, Number(minDurationMs) || 1), Math.trunc(Number(durationMs) || 0))
}

export function xToTimeMs(xPercent, totalMs) {
  const total = Math.max(1, Number(totalMs) || 0)
  return Math.max(0, (Number(xPercent) || 0) / 100 * total)
}

export function yToMidi(yPx, minMidi, maxMidi, rowHeight = SYNTH_PITCH_ROW_HEIGHT) {
  const rowIndex = Math.max(0, Math.min(maxMidi - minMidi, Math.floor(Number(yPx) / rowHeight)))
  return rowIndexToMidi(rowIndex, minMidi, maxMidi)
}

export function midiToY(midi, minMidi, maxMidi, rowHeight = SYNTH_PITCH_ROW_HEIGHT) {
  return midiToRowIndex(midi, minMidi, maxMidi) * rowHeight
}

export function snapDurationMs(durationMs) {
  const snapped = Math.round(Number(durationMs) / SYNTH_SEQUENCE_SNAP_MS) * SYNTH_SEQUENCE_SNAP_MS
  return Math.max(1, snapped)
}

export function clampMidi(midi) {
  const n = Math.trunc(Number(midi))
  return Math.min(127, Math.max(0, n))
}
